"use client";

import { useTranslations } from "next-intl";

function wrapText(text: string, maxCharacters: number) {
  return text.split(/\s+/).reduce<string[]>((lines, word) => {
    const last = lines.length - 1;
    if (last < 0 || `${lines[last]} ${word}`.length > maxCharacters) lines.push(word);
    else lines[last] += ` ${word}`;
    return lines;
  }, []);
}

function Lines({ text, x, y, width, lineHeight, ...props }: {
  text: string;
  x: number;
  y: number;
  width: number;
  lineHeight: number;
} & Omit<React.SVGProps<SVGTextElement>, "x" | "y" | "width">) {
  return <text x={x} y={y} {...props}>{wrapText(text, width).map((line, index) => <tspan key={index} x={x} dy={index ? lineHeight : 0}>{line}</tspan>)}</text>;
}

const originalBoxes = [
  { x: 62, y: 259, width: 328, height: 118, rotate: -1.2, fill: "#d7dfb4", font: "Georgia, serif", size: 29 },
  { x: 74, y: 429, width: 318, height: 124, rotate: 1.5, fill: "#edceb1", font: "Arial, sans-serif", size: 27 },
  { x: 439, y: 271, width: 316, height: 115, rotate: 1.2, fill: "#ddc8df", font: "Arial, sans-serif", size: 30 },
  { x: 419, y: 441, width: 346, height: 130, rotate: -1.4, fill: "#e7db9f", font: "Georgia, serif", size: 28 },
  { x: 806, y: 254, width: 326, height: 129, rotate: -1.6, fill: "#edceb1", font: "Georgia, serif", size: 29 },
  { x: 819, y: 431, width: 318, height: 124, rotate: 1, fill: "#d7dfb4", font: "Arial, sans-serif", size: 27 },
] as const;

export function LandingLesson({ side }: { side: "before" | "after" }) {
  const t = useTranslations("InstitutionalLanding.material");

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 1200 675"
      width="100%"
      height="100%"
      role="img"
      aria-label={t(side === "before" ? "beforeAlt" : "afterAlt")}
      style={{ display: "block" }}
    >
      {side === "before" ? (
        <g fontFamily="Arial, sans-serif">
          <rect width="1200" height="675" fill="#f5edca" />
          <rect x="19" y="19" width="1162" height="637" fill="none" stroke="#85708d" strokeWidth="5" />
          <path d="M49 124H1150" stroke="#8d7296" strokeWidth="5" />
          <text x="53" y="101" fontFamily="Georgia, serif" fontSize="53" fontWeight="700" fill="#6b367c">{t("slide.title")}</text>
          <Lines text={t("slide.intro")} x={57} y={169} width={76} lineHeight={33} fontSize={28} fill="#514c40" />

          {[0, 1, 2].map(index => <text key={index} x={[69, 446, 813][index]} y={[231, 241, 226][index]} fill="#6b367c" fontSize={[26, 25, 28][index]} fontFamily={index === 1 ? "Georgia, serif" : "Arial, sans-serif"} fontWeight="700" textDecoration={index === 1 ? "underline" : undefined}>{t(`slide.phase${index + 1}`)}</text>)}
          <path d="M225 385L237 418M229 408L237 418L245 407M593 393L586 427M579 416L586 427L595 419M967 392L981 421M973 412L981 421L988 409" fill="none" stroke="#85708d" strokeWidth="4" />

          {originalBoxes.map((box, index) => (
            <g key={index} transform={`rotate(${box.rotate} ${box.x + box.width / 2} ${box.y + box.height / 2})`}>
              <rect x={box.x + 6} y={box.y + 7} width={box.width} height={box.height} fill="#86795c" opacity=".22" />
              <rect x={box.x} y={box.y} width={box.width} height={box.height} fill={box.fill} stroke="#8c795e" strokeWidth={index % 2 ? 2 : 3} />
              <text x={box.x + 17} y={box.y + 29} fontSize={17} fill="#66593f" fontWeight="700">{index + 1}.</text>
              <Lines text={t(`slide.step${index + 1}`)} x={box.x + 22} y={box.y + 61} width={22} lineHeight={33} fontFamily={box.font} fontSize={box.size} fill="#3e3446" />
            </g>
          ))}
          <text x="56" y="627" fontSize="21" fontFamily="Georgia, serif" fontStyle="italic" fill="#67583e">{t("slide.footer")}</text>
          <text x="1116" y="628" fontSize="25" fontWeight="700" fill="#6b367c">01</text>
        </g>
      ) : (
        <g fontFamily="Arial, sans-serif">
          <rect width="1200" height="675" fill="#ffffff" />
          <rect x="68" y="44" width="44" height="5" rx="2.5" fill="#008b9c" />
          <text x="68" y="108" fontSize="52" fontWeight="600" letterSpacing="-1.8" fill="#19363d">{t("slide.title")}</text>
          <Lines text={t("slide.intro")} x={68} y={158} width={80} lineHeight={32} fontSize={27} fill="#677c80" />

          {[0, 1, 2].map(index => {
            const x = 68 + index * 362;
            return (
              <g key={index}>
                <rect x={x} y="221" width="340" height="327" rx="21" fill="#f0f6f6" />
                <rect x={x + 25} y="247" width="48" height="48" rx="14" fill="#dcefee" />
                <g transform={`translate(${x + 36} 258)`} fill="none" stroke="#00818f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {index === 0 && <><circle cx="11" cy="11" r="8" /><path d="M17 17l8 8" /></>}
                  {index === 1 && <><path d="M8 1h10M10 1v9L3 22a2 2 0 0 0 2 3h16a2 2 0 0 0 2-3L16 10V1M7 17h13" /><path d="M11 21h4" /></>}
                  {index === 2 && <><path d="M3 3v22h23M9 19v-7M16 19V6M23 19v-9" /></>}
                </g>
                <text x={x + 277} y="278" fontSize="16" fontWeight="600" fill="#7d9699">0{index + 1}</text>
                <text x={x + 25} y="339" fontSize="31" fontWeight="600" letterSpacing="-.8" fill="#19363d">{t(`slide.phase${index + 1}`)}</text>
                <path d={`M${x + 25} 363H${x + 315}`} stroke="#d9e6e7" />
                {[0, 1].map(item => <g key={item}>
                  <circle cx={x + 29} cy={396 + item * 72} r="3" fill="#008b9c" />
                  <Lines text={t(`slide.step${index * 2 + item + 1}`)} x={x + 44} y={404 + item * 72} width={22} lineHeight={31} fontSize={25} fill="#355158" />
                </g>)}
              </g>
            );
          })}
          <path d="M68 597H1132" stroke="#e3ecec" />
          <text x="68" y="633" fontSize="19" fill="#748b90">{t("slide.footer")}</text>
          <text x="1109" y="633" fontSize="19" fill="#00818f">01</text>
        </g>
      )}
    </svg>
  );
}
